import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { planPaymentRequestApi } from '@src/apis/admin';
import type { PaymentRequestSummaryResponseModel } from '@uniquegood/realworld-admin-interface';

type CurrentPaymentRequestQueryKey = readonly ['current-payment-request', string];

export interface UseCurrentPaymentRequestOptions {
    /**
     * React Query 옵션을 덮어씁니다.
     * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
     */
    queryOptions?: Omit<
        UseQueryOptions<
            PaymentRequestSummaryResponseModel | null,
            Error,
            PaymentRequestSummaryResponseModel | null,
            CurrentPaymentRequestQueryKey
        >,
        'queryKey' | 'queryFn'
    >;
}

/**
 * 진행 중인 결제 요청 조회 훅
 *
 * @description
 * - 채널 ID를 받아 진행 중인 결제 요청을 조회합니다.
 * - PlanPaymentRequestApi.getCurrentPaymentRequest를 사용하여 실제 API를 호출합니다.
 * - 404 에러가 발생할 수 있으므로 null을 반환할 수 있습니다.
 */
export const useCurrentPaymentRequest = (
    channelId: string,
    options: UseCurrentPaymentRequestOptions = {}
): UseQueryResult<PaymentRequestSummaryResponseModel | null> => {
    const { queryOptions } = options;
    const { staleTime, ...restQueryOptions } = queryOptions ?? {};

    const queryKey = useMemo<CurrentPaymentRequestQueryKey>(
        () => ['current-payment-request', channelId] as const,
        [channelId]
    );

    return useQuery<
        PaymentRequestSummaryResponseModel | null,
        Error,
        PaymentRequestSummaryResponseModel | null,
        CurrentPaymentRequestQueryKey
    >({
        queryKey,
        queryFn: async () => {
            try {
                const { data } = await planPaymentRequestApi.getCurrentPaymentRequest(
                    channelId,
                    undefined // x-rwd-api-version은 default value 사용
                );
                return data.data ?? null;
            } catch (error: any) {
                // 404 에러인 경우 null 반환
                if (error?.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
        enabled: !!channelId,
        staleTime: staleTime ?? 30 * 1000,
        refetchOnMount: 'always',
        ...restQueryOptions,
    });
};


